import React, { useRef, useState, useEffect } from 'react'
import { motion, useInView } from 'framer-motion'
import { FaAndroid, FaWindows } from 'react-icons/fa'

const MotionA = motion.a

const projectsData = [
  {
    number: '01',
    name: 'Nostalgia Cam',
    description: 'A nostalgic sketch tool — scribble your memories with a hand-drawn, retro camera aesthetic. Built as a fun side project with SVG sketch filters.',
    tags: ['HTML', 'CSS', 'JavaScript', 'Web App'],
    image: '/images/projects/nostalgia-cam.gif',
    url: 'https://nostalgia-cam.pages.dev/'
  },
  {
    number: '02',
    name: 'Tele Drive',
    description: 'Unlimited cloud storage, powered by Telegram. A Flutter mobile app and Tauri desktop client that turn your Telegram account into a personal file server — no proxies, no third-party storage, no monthly fees.',
    tags: ['Dart', 'TypeScript', 'JavaScript', 'CSS', 'Shell'],
    image: '/images/projects/TeleDrive.png',
    downloads: [
      { label: 'APK for Android', url: 'https://github.com/rohandalvi369/teledrive-/releases/download/v1.0.0/teledrive_.apk', icon: 'android' },
      { label: 'EXE for Windows', url: 'https://github.com/rohandalvi369/teledrive-/releases/download/v1.0.0/TeleDrive__x64-setup.exe', icon: 'windows' }
    ]
  }
]

export default function Projects() {
  const headerRef = useRef(null)
  const cardRef = useRef(null)

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  const headerInView = useInView(headerRef, { once: false, amount: 0.15 })
  const cardInView = useInView(cardRef, { once: false, amount: 0.15 })

  return (
    <div style={{ minHeight: '100vh', padding: isMobile ? '80px 1rem 3rem' : '100px 2rem' }}>
      <div className="max-w-5xl mx-auto px-4">
        <motion.div
          ref={headerRef}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
          className="mb-16"
          style={{ display: isMobile ? 'none' : 'block' }}
        >
          <motion.h1
            animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30, scale: 0.97 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
            fontSize: 'clamp(4rem, 12vw, 10rem)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              lineHeight: 0.9,
              color: 'white',
              margin: '0 0 3rem 0',
            }}
          >Projects</motion.h1>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', fontSize: '0.85rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Python · C · Flutter · and more
          </p>
        </motion.div>

        {projectsData.map((project) => (
          <motion.div
            ref={cardRef}
            key={project.number}
            animate={cardInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -15, scale: 0.97 }}
            whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0,217,255,0.08)' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '24px',
              padding: isMobile ? '1.5rem' : '2.5rem',
              marginBottom: '1.5rem',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '1.5rem' : '3rem',
              transition: 'box-shadow 0.3s ease',
            }}
          >
            <span style={{
              fontSize: isMobile ? '3rem' : 'clamp(4rem, 8vw, 7rem)',
              fontWeight: 900,
              color: 'rgba(255,255,255,0.08)',
              lineHeight: 1,
              flexShrink: 0,
              fontVariantNumeric: 'tabular-nums',
              display: isMobile ? 'none' : 'block'
            }}>{project.number}</span>

            <img
              src={project.image}
              alt={project.name}
              style={{
                width: isMobile ? '100%' : '200px',
                height: isMobile ? '200px' : '140px',
                objectFit: 'cover',
                borderRadius: '12px',
                flexShrink: 0,
              }}
            />

            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', marginBottom: '0.5rem' }}>
                {project.name}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1rem' }}>
                {project.description}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {project.tags.map(tag => (
                  <motion.span
                    key={tag}
                    whileHover={{ scale: 1.05, borderColor: '#00d9ff', boxShadow: '0 0 12px rgba(0,217,255,0.2)' }}
                    transition={{ duration: 0.15 }}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      border: '1px solid rgba(0,217,255,0.3)',
                      color: '#00d9ff',
                      fontSize: '0.75rem',
                      cursor: 'default',
                    }}
                  >{tag}</motion.span>
                ))}
              </div>
            </div>

            {project.downloads ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flexShrink: 0, width: isMobile ? '100%' : 'auto', marginTop: isMobile ? '1rem' : 0 }}>
                {project.downloads.map(dl => (
                  <MotionA
                    key={dl.label}
                    href={dl.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02, background: 'rgba(0,217,255,0.05)', borderColor: 'rgba(0,217,255,0.3)' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '50px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    {dl.icon === 'android' ? <FaAndroid size={18} /> : <FaWindows size={18} />}
                    {dl.label}
                  </MotionA>
                ))}
              </div>
            ) : project.url ? (
              <MotionA
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02, background: 'rgba(0,217,255,0.05)', borderColor: 'rgba(0,217,255,0.3)' }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.15 }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '50px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  width: isMobile ? '100%' : 'auto',
                  textAlign: isMobile ? 'center' : undefined,
                  marginTop: isMobile ? '1rem' : 0,
                  boxSizing: 'border-box',
                  display: 'inline-block',
                }}
              >
                Visit Site
              </MotionA>
            ) : null}
          </motion.div>
        ))}

        <p style={{
          color: 'rgba(255,255,255,0.3)',
          textAlign: 'center',
          fontSize: '0.9rem',
          fontFamily: 'monospace',
          padding: '3rem 0',
        }}>
          More projects coming soon...
        </p>
      </div>
    </div>
  )
}
